import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, HardHat, Building2, Landmark, LogOut, ExternalLink, Inbox, Wrench, Users, Compass, Home, Images, ChevronRight, CreditCard, Contact, Handshake, ClipboardList, MapPin, UserCog, CalendarDays, ClipboardCheck, Camera, Tags, Briefcase, FileText, Receipt } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PasswordChange from "../../components/admin/PasswordChange";

const MENU_SECTIONS = [
  {
    label: null,
    items: [
      { to: "/admin/dashboard", label: "お問い合わせ", icon: LayoutDashboard },
      { to: "/admin/mail", label: "メール", icon: Inbox },
    ],
  },
  {
    label: "サイトの編集",
    items: [
      { to: "/admin/home", label: "トップページ", icon: Home },
      { to: "/admin/about", label: "私たちについて", icon: Compass },
      { to: "/admin/services", label: "事業内容", icon: Wrench },
      { to: "/admin/works", label: "施工実績", icon: HardHat },
      { to: "/admin/real-estate", label: "不動産物件", icon: Building2 },
      { to: "/admin/recruit", label: "採用情報", icon: Users },
      { to: "/admin/company", label: "会社情報", icon: Landmark },
      { to: "/admin/media", label: "写真", icon: Images },
    ],
  },
  {
    label: "日々の業務",
    items: [
      { to: "/admin/business", label: "業務ホーム", icon: Briefcase },
      { to: "/admin/workflow", label: "受付から入金まで", icon: ClipboardCheck },
      { to: "/admin/schedules", label: "作業予定", icon: CalendarDays },
      { to: "/admin/instructions", label: "作業指示", icon: ClipboardCheck },
      { to: "/admin/estimates", label: "見積書", icon: FileText },
      { to: "/admin/invoices", label: "請求書", icon: Receipt },
      { to: "/admin/site-photos", label: "現場写真", icon: Camera, badge: "photos" },
    ],
  },
  {
    label: "登録・マスター",
    items: [
      { to: "/admin/projects", label: "工事案件", icon: ClipboardList },
      { to: "/admin/sites", label: "現場", icon: MapPin },
      { to: "/admin/staff", label: "スタッフ", icon: UserCog },
      { to: "/admin/work-types", label: "工種", icon: Tags },
    ],
  },
  {
    label: "取引先",
    items: [
      { to: "/admin/customers", label: "顧客・取引先", icon: Contact },
      { to: "/admin/partners", label: "協力会社", icon: Handshake },
      { to: "/admin/bank-accounts", label: "銀行口座", icon: CreditCard },
    ],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const groups = MENU_SECTIONS.filter((s) => s.label);
  const [openGroups, setOpenGroups] = useState(() =>
    Object.fromEntries(groups.map((g) => [g.label, g.items.some((m) => location.pathname.startsWith(m.to))]))
  );
  const [pendingPhotos, setPendingPhotos] = useState(0);

  useEffect(() => {
    api.get("/admin/business-summary").then((r) => setPendingPhotos(r.data.pending_photos || 0)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        if (g.items.some((m) => location.pathname.startsWith(m.to))) next[g.label] = true;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen flex-col bg-ink md:flex-row">
      <aside className="flex shrink-0 flex-col border-b border-line bg-coal md:min-h-screen md:w-60 md:border-b-0 md:border-r">
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 md:justify-start md:px-6 md:py-5">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <img src="/neo-logo.png" alt="NEO CONSTRUCTION" className="h-8 w-auto rounded-[6px]" />
            <span className="font-mono text-[10px] tracking-[0.35em] text-gray-500">ADMIN</span>
          </Link>
          <div className="flex items-center gap-1 md:hidden">
            <Link to="/" className="p-2 text-gray-500 transition-colors hover:text-white" aria-label="公開サイトを見る">
              <ExternalLink className="h-4 w-4" />
            </Link>
            <PasswordChange compact />
            <button data-testid="admin-logout-button-mobile" onClick={onLogout} className="p-2 text-gray-500 transition-colors hover:text-accent" aria-label="ログアウト">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        {/* モバイル用（横スクロール・全項目表示） */}
        <nav className="p-3 md:hidden" aria-label="管理メニュー">
          <select data-testid="admin-mobile-navigation" aria-label="管理メニュー" value={MENU_SECTIONS.flatMap(s => s.items).find(m => location.pathname.startsWith(m.to))?.to || "/admin/dashboard"} onChange={e => navigate(e.target.value)} className="w-full min-w-0 border border-line bg-ink px-3 py-3 text-sm text-white">
            {MENU_SECTIONS.map((sec, i) => <optgroup key={i} label={sec.label || "お問い合わせ・メール"}>{sec.items.map(m => <option key={m.to} value={m.to}>{m.label}{m.badge === "photos" && pendingPhotos > 0 ? `（未確認 ${pendingPhotos}件）` : ""}</option>)}</optgroup>)}
          </select>
        </nav>
        {/* PC用（クリックで展開） */}
        <nav className="hidden md:flex md:flex-1 md:flex-col md:gap-0 md:space-y-1 md:p-4">
          {MENU_SECTIONS[0].items.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              data-testid={`admin-nav-${m.to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 border-l-2 px-4 py-3 text-sm transition-colors ${
                  isActive ? "border-accent bg-accent/10 text-accent" : "border-transparent text-gray-400 hover:text-white"
                }`
              }
            >
              <m.icon className="h-4 w-4" />
              {m.label}
            </NavLink>
          ))}
          {groups.map((g) => (
            <div key={g.label}>
              <button
                type="button"
                data-testid={`admin-nav-group-${g.label}`}
                onClick={() => setOpenGroups((prev) => ({ ...prev, [g.label]: !prev[g.label] }))}
                aria-expanded={!!openGroups[g.label]}
                className="mt-4 flex items-center gap-2 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-gray-500 transition-colors hover:text-white"
              >
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${openGroups[g.label] ? "rotate-90" : ""}`} />
                {g.label}
              </button>
              {openGroups[g.label] &&
                g.items.map((m) => (
                  <NavLink
                    key={m.to}
                    to={m.to}
                    data-testid={`admin-nav-${m.to.split("/").pop()}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 border-l-2 px-4 py-3 text-sm transition-colors ${
                        isActive ? "border-accent bg-accent/10 text-accent" : "border-transparent text-gray-400 hover:text-white"
                      }`
                    }
                  >
                    <m.icon className="h-4 w-4" />
                    {m.label}
                    {m.badge === "photos" && pendingPhotos > 0 && (
                      <span data-testid="admin-nav-photos-badge" className="ml-auto rounded-full bg-accent px-1.5 text-[10px] font-bold text-ink">{pendingPhotos}</span>
                    )}
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>
        <div className="hidden border-t border-line p-4 md:block">
          <Link to="/" className="flex items-center gap-3 px-4 py-2 text-xs text-gray-500 transition-colors hover:text-white">
            <ExternalLink className="h-3.5 w-3.5" />
            公開サイトを見る
          </Link>
          <PasswordChange />
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="mt-1 flex w-full items-center gap-3 px-4 py-2 text-xs text-gray-500 transition-colors hover:text-accent"
          >
            <LogOut className="h-3.5 w-3.5" />
            ログアウト
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 md:p-10">
        <Outlet />
      </main>
    </div>
  );
}
